'use client';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { forwardRef } from 'react';
import { InlineInput } from '@s7/base';
import { useUncontrolled, useId } from '@s7/hooks';
import { useComponentDefaultProps } from '@s7/styles';
import { COMPONENT_ID, defaultProps } from './Checkbox.contract.js';
import useStyles from './Checkbox.styles.js';
import { useCheckboxGroupContext } from './CheckboxGroup/CheckboxGroup.context.js';
import { CheckboxGroup } from './CheckboxGroup/CheckboxGroup.js';
import { isIncludesSafe } from '../utils/SelectionControls/isIncludesSafe.js';
import { Typography } from '../Typography/Typography.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var Checkbox = /*#__PURE__*/ forwardRef(function(props, ref) {
    var context = useCheckboxGroupContext();
    var _useComponentDefaultProps = useComponentDefaultProps(COMPONENT_ID, defaultProps, props, {
        context: context,
        exclude: [
            "value",
            "onChange",
            "theme"
        ]
    }), theme = _useComponentDefaultProps.theme, rootRef = _useComponentDefaultProps.rootRef, sx = _useComponentDefaultProps.sx, style = _useComponentDefaultProps.style, styles = _useComponentDefaultProps.styles, className = _useComponentDefaultProps.className, classNames = _useComponentDefaultProps.classNames, staticId = _useComponentDefaultProps.id, label = _useComponentDefaultProps.label, Icon = _useComponentDefaultProps.icon, size = _useComponentDefaultProps.size, variant = _useComponentDefaultProps.variant, indeterminate = _useComponentDefaultProps.indeterminate, error = _useComponentDefaultProps.error, checked = _useComponentDefaultProps.checked, onChange = _useComponentDefaultProps.onChange, defaultChecked = _useComponentDefaultProps.defaultChecked, position = _useComponentDefaultProps.position, color = _useComponentDefaultProps.color, readOnly = _useComponentDefaultProps.readOnly, required = _useComponentDefaultProps.required, disabled = _useComponentDefaultProps.disabled, spacing = _useComponentDefaultProps.spacing, transitionDuration = _useComponentDefaultProps.transitionDuration, wrapperProps = _useComponentDefaultProps.wrapperProps, textProps = _useComponentDefaultProps.textProps, others = _object_without_properties_loose(_useComponentDefaultProps, [
        "theme",
        "rootRef",
        "sx",
        "style",
        "styles",
        "className",
        "classNames",
        "id",
        "label",
        "icon",
        "size",
        "variant",
        "indeterminate",
        "error",
        "checked",
        "onChange",
        "defaultChecked",
        "position",
        "color",
        "readOnly",
        "required",
        "disabled",
        "spacing",
        "transitionDuration",
        "wrapperProps",
        "textProps"
    ]);
    var _useStyles = useStyles({
        params: {
            color: color,
            error: error,
            transitionDuration: transitionDuration
        },
        options: {
            name: COMPONENT_ID,
            styles: styles,
            classNames: classNames,
            variations: {
                size: size,
                variant: variant
            }
        },
        theme: theme != null ? theme : context == null ? void 0 : context.theme
    }), classes = _useStyles.classes, cx = _useStyles.cx;
    var _context_onChange;
    var inferredProps = {
        checked: context ? isIncludesSafe(context.value, others.value) : checked,
        onChange: (_context_onChange = context == null ? void 0 : context.onChange) != null ? _context_onChange : onChange
    };
    var _useUncontrolled = useUncontrolled({
        value: inferredProps.checked,
        defaultValue: defaultChecked,
        finalValue: false
    }), _checked = _useUncontrolled[0], handleChange = _useUncontrolled[1];
    var id = useId(staticId);
    var Label = /*#__PURE__*/ jsx(Typography, _extends({
        className: classes.label,
        "data-disabled": disabled,
        "data-error": error,
        size: size
    }, textProps, {
        children: label
    }));
    return /*#__PURE__*/ jsx(InlineInput, _extends({}, wrapperProps, {
        ref: rootRef,
        __staticSelector: COMPONENT_ID,
        as: "span",
        className: cx(className, classes.root),
        disabled: disabled,
        id: id,
        label: label ? Label : undefined,
        position: position,
        readOnly: readOnly,
        spacing: spacing,
        style: style,
        sx: sx,
        children: /*#__PURE__*/ jsxs("div", {
            className: classes.inner,
            children: [
                /*#__PURE__*/ jsx("input", _extends({}, others, {
                    ref: ref,
                    checked: _checked,
                    className: classes.input,
                    disabled: disabled,
                    id: id,
                    readOnly: readOnly,
                    required: required,
                    type: "checkbox",
                    onChange: function(event) {
                        if (readOnly) {
                            return false;
                        }
                        inferredProps.onChange == null ? void 0 : inferredProps.onChange.call(inferredProps, event);
                        handleChange(event.currentTarget.checked);
                    }
                })),
                Icon && /*#__PURE__*/ jsx(Icon, {
                    className: classes.icon,
                    indeterminate: indeterminate
                })
            ]
        })
    }));
});
Checkbox.Group = CheckboxGroup;
var Checkbox$1 = Checkbox;
Checkbox.displayName = COMPONENT_ID;

export { Checkbox, Checkbox$1 as default };
